import React, { useState, useEffect, useCallback } from 'react';
import {
  Search, Loader2, Eye, Plus, ArrowUpDown, ArrowUp, ArrowDown,
  BarChart3, Calendar, Activity, ArrowLeft, CheckCircle2,
  ChevronRight, Database, FolderKanban, Table2,
  ShieldCheck, Layers, Wrench, FileText,
} from 'lucide-react';
import StepIndicator from '../onboarding/StepIndicator';
import ExecutiveTab from '../onboarding/dashboard/ExecutiveTab';
import RemediationTab from '../onboarding/dashboard/RemediationTab';
import RuleResultsTab from '../onboarding/dashboard/RuleResultsTab';
import { sdqApi } from '../../services/api';
import { formatDate, formatDateTime, scoreColor, scoreBg } from '../../utils/format';
import PaginationBar from '../../components/PaginationBar';
import type { SDQRunHistory, Pagination, SDQReport, OnboardingValidationResults } from '../../types';

type SortDir = 'asc' | 'desc';

interface SDQLandingProps {
  onNewRun: () => void;
}

export default function SDQLanding({ onNewRun }: SDQLandingProps) {
  const [items, setItems] = useState<SDQRunHistory[]>([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState<Pagination>({ count: 0, page: 1, per_page: 10, pages: 1 });
  const [search, setSearch] = useState('');
  const [sortCol, setSortCol] = useState('timestamp');
  const [sortDir, setSortDir] = useState<SortDir>('desc');

  // View mode: null = list, string = viewing a run's full flow
  const [viewRunId, setViewRunId] = useState<string | null>(null);

  const fetchHistory = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await sdqApi.history({
        page: pagination.page,
        items_per_page: pagination.per_page,
        search,
        sort_col: sortCol,
        sort_dir: sortDir,
      });
      const data = resp.data.payload;
      setItems(data.items);
      setPagination(data.pagination);
    } catch (err) {
      console.error('Failed to load SDQ history', err);
    } finally {
      setLoading(false);
    }
  }, [pagination.page, pagination.per_page, search, sortCol, sortDir]);

  useEffect(() => { fetchHistory(); }, [fetchHistory]);

  const handleSort = (col: string) => {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
    setPagination(p => ({ ...p, page: 1 }));
  };

  // If viewing a run, show the full flow
  if (viewRunId) {
    return <SDQFlowView runId={viewRunId} onBack={() => setViewRunId(null)} onNewRun={onNewRun} />;
  }

  const totalRuns = pagination.count;
  const avgScore = items.length > 0
    ? items.reduce((sum, r) => sum + (r.dq_score || 0), 0) / items.length
    : 0;
  const lastRun = items.length > 0 ? items[0]?.timestamp : null;

  function SortIcon({ col }: { col: string }) {
    if (sortCol !== col) return <ArrowUpDown className="w-3.5 h-3.5 text-cargo-muted/40" />;
    return sortDir === 'asc'
      ? <ArrowUp className="w-3.5 h-3.5 text-cargo-accent" />
      : <ArrowDown className="w-3.5 h-3.5 text-cargo-accent" />;
  }

  return (
    <div className="space-y-6">
      <button onClick={onNewRun}
        className="w-full flex items-center justify-between px-5 py-4 rounded-xl bg-cargo-surface border border-cargo-border hover:border-cargo-accent/50 hover:bg-cargo-accent/5 transition-all group">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cargo-accent/15 flex items-center justify-center">
            <Plus className="w-5 h-5 text-cargo-accent" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-cargo-text">New SDQ Assessment</p>
            <p className="text-xs text-cargo-muted">Connect to a source, attach a project, and run validation</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-cargo-muted group-hover:text-cargo-accent transition-colors" />
      </button>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card icon={<Activity className="w-5 h-5 text-cargo-accent" />} label="Total Runs" value={String(totalRuns)} />
        <Card icon={<BarChart3 className="w-5 h-5 text-blue-400" />} label="Average DQ Score" value={avgScore > 0 ? `${avgScore.toFixed(1)}%` : '—'} />
        <Card icon={<Calendar className="w-5 h-5 text-emerald-400" />} label="Last Run" value={lastRun ? formatDate(lastRun) : '—'} />
      </div>

      <div className="flex items-center gap-4">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-cargo-muted" />
          <input type="text" value={search}
            onChange={(e) => { setSearch(e.target.value); setPagination(p => ({ ...p, page: 1 })); }}
            placeholder="Search projects, groups, assets..."
            className="w-full pl-10 pr-4 py-2 rounded-lg bg-cargo-surface border border-cargo-border text-cargo-text text-sm placeholder:text-cargo-muted outline-none focus:border-cargo-accent" />
        </div>
      </div>

      <div className="bg-cargo-surface rounded-xl border border-cargo-border overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-cargo-border">
                {[
                  { col: 'project_name', label: 'Project' }, { col: 'group_name', label: 'Group' },
                  { col: 'data_asset', label: 'Data Asset' }, { col: 'dq_score', label: 'DQ Score' },
                  { col: 'grade', label: 'Grade' }, { col: 'total_rules', label: 'Rules' },
                  { col: 'timestamp', label: 'Date' },
                ].map(({ col, label }) => (
                  <th key={col} className="px-4 py-3 text-left text-xs font-medium text-cargo-muted uppercase tracking-wider cursor-pointer hover:text-cargo-text"
                    onClick={() => handleSort(col)}>
                    <span className="flex items-center gap-1">{label} <SortIcon col={col} /></span>
                  </th>
                ))}
                <th className="px-4 py-3 text-xs font-medium text-cargo-muted uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-cargo-border">
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center"><Loader2 className="w-6 h-6 animate-spin text-cargo-accent mx-auto" /></td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-12 text-center text-cargo-muted">No SDQ runs found. Click "+ New SDQ Assessment" to get started.</td></tr>
              ) : items.map((run) => (
                <tr key={run._id} className="hover:bg-cargo-hover/30 transition-colors">
                  <td className="px-4 py-3 text-cargo-text font-medium">{run.project_name}</td>
                  <td className="px-4 py-3 text-cargo-muted">{run.group_name}</td>
                  <td className="px-4 py-3 text-cargo-muted">{run.data_asset}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded border ${scoreBg(run.dq_score)}`}>
                      {(run.dq_score || 0).toFixed(1)}%
                    </span>
                  </td>
                  <td className="px-4 py-3"><span className={`text-sm font-bold ${scoreColor(run.dq_score)}`}>{run.grade || '—'}</span></td>
                  <td className="px-4 py-3 text-cargo-muted">
                    <span className="text-green-400">{run.success_count}</span>{' / '}<span className="text-red-400">{run.failed_count}</span>
                    <span className="text-cargo-muted/50 ml-1">({run.total_rules})</span>
                  </td>
                  <td className="px-4 py-3 text-cargo-muted">{formatDate(run.timestamp)}</td>
                  <td className="px-4 py-3">
                    <button onClick={() => setViewRunId(run._id)}
                      className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-accent hover:bg-cargo-accent/10 transition-colors" title="View full flow">
                      <Eye className="w-4 h-4" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {!loading && items.length > 0 && (
          <PaginationBar page={pagination.page} totalPages={pagination.pages} perPage={pagination.per_page}
            onPageChange={(p) => setPagination(prev => ({ ...prev, page: p }))}
            onPerPageChange={(n) => setPagination(prev => ({ ...prev, per_page: n, page: 1 }))} />
        )}
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════
   SDQ Full Flow Read-Only View
   ═══════════════════════════════════════ */

const FLOW_STEPS = ['Connector', 'Connection', 'Project', 'Run', 'Results'];

function SDQFlowView({ runId, onBack, onNewRun }: { runId: string; onBack: () => void; onNewRun: () => void }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [step, setStep] = useState(0);

  useEffect(() => {
    setLoading(true);
    sdqApi.historyDetail(runId)
      .then((resp) => { setData(resp.data.payload); setStep(4); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [runId]);

  if (loading) {
    return <div className="flex items-center justify-center py-24"><Loader2 className="w-8 h-8 animate-spin text-cargo-accent" /></div>;
  }
  if (!data) {
    return <div className="text-center py-24 text-cargo-muted">Failed to load run data.</div>;
  }

  const flow = data.flow || {};
  const report: SDQReport | null = data.sdq_report_summary || null;
  const vr: OnboardingValidationResults | null = data.validation_results || null;

  return (
    <div className="max-w-[1400px] mx-auto">
      <div className="mb-6 flex items-center gap-3">
        <button onClick={onBack} className="p-1.5 rounded-lg text-cargo-muted hover:text-cargo-text hover:bg-cargo-panel transition-colors">
          <ArrowLeft className="w-5 h-5" />
        </button>
        <div>
          <h1 className="text-xl font-bold text-cargo-text">SDQ Run — {data.project_name}</h1>
          <p className="text-cargo-muted text-sm">{formatDateTime(data.timestamp)}</p>
        </div>
      </div>

      <StepIndicator steps={FLOW_STEPS} current={step} />

      <div className="mt-8">
        {/* Step 0: Connector */}
        {step === 0 && (
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6">
            <h2 className="text-lg font-semibold text-cargo-text mb-4">Connector</h2>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-xl bg-cargo-accent/10 flex items-center justify-center">
                <Database className="w-8 h-8 text-cargo-accent" />
              </div>
              <div>
                <p className="text-lg font-bold text-cargo-text uppercase">{flow.connector_type || data.connection_type || '—'}</p>
                <p className="text-sm text-cargo-muted">Database connector used for this assessment</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 1: Connection */}
        {step === 1 && (
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6">
            <h2 className="text-lg font-semibold text-cargo-text mb-4">Connection Details</h2>
            <div className="grid grid-cols-2 gap-4">
              {flow.connection ? (
                Object.entries(flow.connection).filter(([k]) => k !== 'password' && k !== 'connector_type').map(([key, val]) => (
                  <InfoItem key={key} label={key.replace(/_/g, ' ')} value={String(val || '—')} />
                ))
              ) : (
                <p className="text-cargo-muted col-span-2">Connection details not saved for this run.</p>
              )}
            </div>
          </div>
        )}

        {/* Step 2: Project */}
        {step === 2 && (
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-6">
            <h2 className="text-lg font-semibold text-cargo-text mb-4">Project & Table Selection</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <InfoCard icon={<FolderKanban className="w-5 h-5 text-cargo-accent" />} label="Project" value={data.project_name} />
              <InfoCard icon={<Layers className="w-5 h-5 text-cargo-warning" />} label="Group" value={data.group_name} />
              <InfoCard icon={<ShieldCheck className="w-5 h-5 text-purple-400" />} label="Schema" value={flow.schema || '—'} />
              <InfoCard icon={<Table2 className="w-5 h-5 text-cargo-success" />} label="Table" value={data.data_asset || flow.tables?.[0] || '—'} />
            </div>
          </div>
        )}

        {/* Step 3: Run */}
        {step === 3 && (
          <div className="bg-cargo-surface rounded-xl border border-cargo-border p-8 text-center space-y-4">
            <div className="w-14 h-14 mx-auto bg-emerald-500/10 rounded-xl flex items-center justify-center">
              <CheckCircle2 className="w-7 h-7 text-emerald-400" />
            </div>
            <div>
              <p className="text-cargo-text font-medium">Assessment Completed</p>
              <p className="text-cargo-muted text-sm mt-1">
                {data.total_rules} rules evaluated — {data.success_count} passed, {data.failed_count} failed
              </p>
            </div>
            <div className="flex items-center justify-center gap-6 text-sm">
              <div className="text-center">
                <p className="text-2xl font-bold text-cargo-text">{(data.dq_score || 0).toFixed(1)}%</p>
                <p className="text-xs text-cargo-muted">DQ Score</p>
              </div>
              <div className="text-center">
                <p className={`text-2xl font-bold ${scoreColor(data.dq_score)}`}>{data.grade || '—'}</p>
                <p className="text-xs text-cargo-muted">Grade</p>
              </div>
            </div>
          </div>
        )}

        {/* Step 4: Results — 3-tab dashboard (Executive / Remediation / Rules) */}
        {step === 4 && (
          <ResultsView report={report} vr={vr} />
        )}
      </div>

      {/* Navigation */}
      <div className="flex justify-between mt-6">
        <button onClick={() => step > 0 ? setStep(step - 1) : onBack()}
          className="flex items-center gap-2 px-5 py-2.5 rounded-lg border border-cargo-border text-cargo-muted hover:text-cargo-text transition-colors">
          <ArrowLeft className="w-4 h-4" /> {step === 0 ? 'Back to History' : 'Back'}
        </button>
        {step < 4 && (
          <button onClick={() => setStep(step + 1)}
            className="flex items-center gap-2 px-5 py-2.5 rounded-lg bg-cargo-accent hover:bg-cargo-accent-hover text-white font-medium transition-colors">
            Next <ChevronRight className="w-4 h-4" />
          </button>
        )}
      </div>

      <button onClick={onNewRun}
        className="w-full flex items-center justify-between mt-6 px-5 py-4 rounded-xl bg-cargo-surface border border-cargo-border hover:border-cargo-accent/50 hover:bg-cargo-accent/5 transition-all group">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-cargo-accent/15 flex items-center justify-center">
            <Plus className="w-5 h-5 text-cargo-accent" />
          </div>
          <div className="text-left">
            <p className="text-sm font-semibold text-cargo-text">New SDQ Assessment</p>
            <p className="text-xs text-cargo-muted">Start a new structured data quality run</p>
          </div>
        </div>
        <ChevronRight className="w-5 h-5 text-cargo-muted group-hover:text-cargo-accent transition-colors" />
      </button>
    </div>
  );
}

/* ── Results View (step 4) — 3-tab layout matching the wizard ── */

type ResultsTabId = 'executive' | 'remediation' | 'rules';

const RESULTS_TABS: { id: ResultsTabId; label: string; icon: React.ComponentType<{ className?: string }> }[] = [
  { id: 'executive', label: 'Executive', icon: BarChart3 },
  { id: 'remediation', label: 'Remediation', icon: Wrench },
  { id: 'rules', label: 'Rule Results', icon: FileText },
];

function ResultsView({ report, vr }: { report: SDQReport | null; vr: OnboardingValidationResults | null }) {
  const [activeTab, setActiveTab] = useState<ResultsTabId>('executive');

  const hasReport = !!report;
  const hasValidation = !!vr;

  if (!hasReport && !hasValidation) {
    return (
      <div className="bg-cargo-surface rounded-xl border border-cargo-border p-8 text-center text-cargo-muted">
        No results data available for this run.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Tab bar */}
      {hasReport && (
        <div className="flex gap-1 bg-cargo-surface border border-cargo-border rounded-lg p-1">
          {RESULTS_TABS.map(({ id, label, icon: Icon }) => (
            <button
              key={id}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-2 flex-1 py-2.5 px-4 rounded-md text-sm font-medium transition-all
                ${activeTab === id
                  ? 'bg-cargo-accent text-white shadow-sm'
                  : 'text-cargo-muted hover:text-cargo-text hover:bg-cargo-hover/50'
                }`}
            >
              <Icon className="w-4 h-4" />
              {label}
            </button>
          ))}
        </div>
      )}

      {/* Tab content */}
      {hasReport && (
        <div>
          {activeTab === 'executive' && <ExecutiveTab report={report!} />}
          {activeTab === 'remediation' && <RemediationTab report={report!} />}
          {activeTab === 'rules' && hasValidation && <RuleResultsTab results={vr!} />}
          {activeTab === 'rules' && !hasValidation && (
            <div className="text-center py-8">
              <p className="text-cargo-muted">No validation results available.</p>
            </div>
          )}
        </div>
      )}

      {/* Fallback: no SDQ report but has validation results */}
      {!hasReport && hasValidation && (
        <RuleResultsTab results={vr!} />
      )}
    </div>
  );
}

/* ── Shared Components ── */

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="bg-cargo-panel rounded-lg p-3">
      <p className="text-xs text-cargo-muted capitalize">{label}</p>
      <p className="text-sm font-medium text-cargo-text mt-0.5">{value}</p>
    </div>
  );
}

function InfoCard({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 bg-cargo-panel rounded-lg p-3">
      {icon}
      <div>
        <p className="text-xs text-cargo-muted">{label}</p>
        <p className="text-sm font-medium text-cargo-text truncate">{value}</p>
      </div>
    </div>
  );
}

function Card({ icon, label, value }: { icon: React.ReactNode; label: string; value: string }) {
  return (
    <div className="bg-cargo-surface rounded-xl border border-cargo-border p-5 flex items-center gap-4">
      <div className="w-10 h-10 rounded-lg bg-cargo-panel flex items-center justify-center">{icon}</div>
      <div>
        <p className="text-xs text-cargo-muted">{label}</p>
        <p className="text-lg font-bold text-cargo-text">{value}</p>
      </div>
    </div>
  );
}
